import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Copy, CheckCircle2, Download, KeyRound, ArrowLeft, AlertCircle } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Iter75 — Enroll a user into TOTP-based MFA.
 *
 * Flow:
 *   1. POST /auth/mfa/setup     → { secret, otpauth_uri, qr_data_url }
 *   2. User scans QR (or types secret) in Google Authenticator / Authy.
 *   3. User submits the 6-digit code → POST /auth/mfa/verify → backup codes.
 *   4. Backup codes shown ONCE. User must download / copy before dismiss.
 *
 * A returning-from-partial-enrollment user just re-hits the endpoint —
 * the backend generates a fresh secret and invalidates the previous pending one.
 */
export default function SetupMfa() {
    const { refresh, user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const forced = params.get("forced") === "1";

    const [step, setStep] = useState("loading"); // loading | scan | codes | done
    const [setup, setSetup] = useState(null); // { secret, otpauth_uri, qr_data_url }
    const [code, setCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState("");
    const [backupCodes, setBackupCodes] = useState([]);
    // Iter79 — 5 emergency recovery codes shown in a separate panel.
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [copied, setCopied] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    const startSetup = useCallback(async () => {
        setError("");
        try {
            const { data } = await api.post("/auth/mfa/setup");
            setSetup(data);
            setStep("scan");
        } catch (e) {
            setError(formatApiError(e));
            setStep("scan");
        }
    }, []);

    useEffect(() => {
        startSetup();
    }, [startSetup]);

    const verify = async (e) => {
        e?.preventDefault?.();
        if (verifying || code.length !== 6) return;
        setVerifying(true);
        setError("");
        try {
            const { data } = await api.post("/auth/mfa/verify", { code });
            setBackupCodes(data.backup_codes || []);
            setRecoveryCodes(data.recovery_codes || []);  // Iter79
            setStep("codes");
            toast.success("Two-factor authentication enabled.");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setVerifying(false);
        }
    };

    const copySecret = async () => {
        try {
            await navigator.clipboard.writeText(setup?.secret || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (_e) {
            toast.error("Copy failed — please copy manually");
        }
    };

    const downloadBackupCodes = () => {
        const now = new Date().toISOString().slice(0, 10);
        const body = [
            "ASL HR Management Platform — MFA Backup Codes",
            `Account: ${user?.email || ""}`,
            `Generated: ${now}`,
            "",
            "Each code can be used ONCE if you lose access to your authenticator.",
            "Store them somewhere safe (password manager, printed and locked away).",
            "",
            ...backupCodes,
            "",
        ].join("\n");
        const blob = new Blob([body], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `asl-mfa-backup-codes-${now}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyAllCodes = async () => {
        try {
            await navigator.clipboard.writeText(backupCodes.join("\n"));
            toast.success("Backup codes copied to clipboard.");
        } catch (_e) {
            toast.error("Copy failed — please save them manually");
        }
    };

    const finish = async () => {
        await refresh();
        navigate("/", { replace: true });
    };

    return (
        <AppShell title="Two-Factor Authentication">
            <section className="max-w-2xl mx-auto space-y-6" data-testid="setup-mfa-page">
                <header className="flex items-start gap-3">
                    <ShieldCheck className="h-8 w-8 asl-navy-text mt-0.5" />
                    <div>
                        <h2 className="text-2xl font-bold asl-navy-text tracking-tight">
                            Set up two-factor authentication
                        </h2>
                        <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                            Add a second layer of security using an authenticator app on your phone.
                        </p>
                    </div>
                </header>

                {forced && (
                    <div
                        className="rounded-md border px-4 py-3 flex items-start gap-3"
                        style={{ background: "#FFF7E6", borderColor: "#F5C87B", color: "#7A4B00" }}
                        data-testid="setup-mfa-forced-banner"
                    >
                        <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold">MFA is required on your account.</div>
                            <div>Complete setup below to keep accessing the platform.</div>
                        </div>
                    </div>
                )}

                {step === "loading" && (
                    <div className="asl-card p-8 text-center text-[color:var(--asl-muted)]"
                        data-testid="setup-mfa-loading">
                        Preparing your secret…
                    </div>
                )}

                {step === "scan" && setup && (
                    <div className="asl-card p-6 space-y-5" data-testid="setup-mfa-scan-card">
                        <div>
                            <h3 className="font-semibold asl-navy-text mb-2">1. Scan the QR code</h3>
                            <p className="text-sm text-[color:var(--asl-muted)] mb-3">
                                Open Google Authenticator, Authy, 1Password or any TOTP app and scan the code below.
                            </p>
                            <div className="flex items-center justify-center bg-white rounded-md border p-4"
                                style={{ borderColor: "var(--asl-border)" }}>
                                <img
                                    src={setup.qr_data_url}
                                    alt="QR code for authenticator app"
                                    style={{ width: 220, height: 220 }}
                                    data-testid="setup-mfa-qr"
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="font-semibold asl-navy-text mb-2">Can&apos;t scan? Type this secret</h3>
                            <div className="flex items-center gap-2">
                                <code
                                    className="flex-1 font-mono text-sm px-3 py-2 rounded-md break-all"
                                    style={{ background: "var(--asl-bg,#F5F6F8)", color: "#1A1A1A" }}
                                    data-testid="setup-mfa-secret"
                                >
                                    {setup.secret}
                                </code>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={copySecret}
                                    data-testid="setup-mfa-copy-secret"
                                >
                                    {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
                                </Button>
                            </div>
                        </div>

                        <form onSubmit={verify} className="space-y-3 pt-2 border-t"
                            style={{ borderColor: "var(--asl-border)" }}>
                            <div>
                                <Label htmlFor="mfa-code" className="asl-navy-text">
                                    2. Enter the 6-digit code from your app
                                </Label>
                                <Input
                                    id="mfa-code"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className="mt-1 tracking-[0.4em] text-center font-mono text-lg"
                                    data-testid="setup-mfa-code-input"
                                />
                            </div>

                            {error && (
                                <div
                                    className="rounded-md border px-3 py-2 text-sm"
                                    style={{ background: "#FEF2F2", borderColor: "#FCA5A5", color: "#7F1D1D" }}
                                    data-testid="setup-mfa-error"
                                >
                                    {error}
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-1">
                                <Button
                                    type="submit"
                                    className="asl-navy-bg text-white"
                                    disabled={code.length !== 6 || verifying}
                                    data-testid="setup-mfa-verify-button"
                                >
                                    {verifying ? "Verifying…" : "Enable MFA"}
                                </Button>
                                {!forced && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={() => navigate("/account")}
                                        data-testid="setup-mfa-cancel-button"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Not now
                                    </Button>
                                )}
                            </div>
                        </form>
                    </div>
                )}

                {step === "codes" && (
                    <div className="asl-card p-6 space-y-5" data-testid="setup-mfa-codes-card">
                        <div className="flex items-start gap-3">
                            <KeyRound className="h-6 w-6 asl-crimson-text mt-0.5" />
                            <div>
                                <h3 className="font-semibold asl-navy-text text-lg">Save your backup codes</h3>
                                <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                    Each code works once if you lose access to your authenticator.
                                    <strong className="asl-crimson-text"> They won&apos;t be shown again.</strong>
                                </p>
                            </div>
                        </div>

                        <div
                            className="rounded-md border p-4 grid grid-cols-2 gap-2 font-mono text-sm"
                            style={{ background: "var(--asl-bg,#F5F6F8)", borderColor: "var(--asl-border)" }}
                            data-testid="setup-mfa-backup-codes"
                        >
                            {backupCodes.map((c, i) => (
                                <div key={i} className="tracking-wider" data-testid={`setup-mfa-backup-code-${i}`}>
                                    {c}
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={downloadBackupCodes}
                                data-testid="setup-mfa-download-codes"
                            >
                                <Download className="h-4 w-4 mr-1.5" />
                                Download as .txt
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={copyAllCodes}
                                data-testid="setup-mfa-copy-codes"
                            >
                                <Copy className="h-4 w-4 mr-1.5" />
                                Copy all
                            </Button>
                        </div>

                        <label
                            className="flex items-start gap-2 text-sm cursor-pointer pt-2 border-t"
                            style={{ borderColor: "var(--asl-border)" }}
                            data-testid="setup-mfa-acknowledge-label"
                        >
                            <input
                                type="checkbox"
                                checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                className="mt-1"
                                data-testid="setup-mfa-acknowledge-checkbox"
                            />
                            <span className="asl-navy-text">
                                I&apos;ve saved these backup codes in a safe place.
                            </span>
                        </label>

                        {/* Iter79 — Emergency recovery codes panel */}
                        {recoveryCodes.length > 0 && (
                            <div className="pt-4 mt-2 border-t space-y-3" style={{ borderColor: "var(--asl-border)" }}
                                data-testid="setup-mfa-recovery-panel">
                                <div className="flex items-start gap-3">
                                    <ShieldAlert className="h-6 w-6 asl-crimson-text mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold asl-navy-text text-lg">Emergency recovery codes</h3>
                                        <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                            Save these <strong>offline</strong> — in a safe, printed on paper, or in an offline
                                            password manager. If you lose your phone AND your 8 backup codes above, one of
                                            these codes lets you reset your password and clear MFA. <strong className="asl-crimson-text">
                                            They will not be shown again.</strong>
                                        </p>
                                    </div>
                                </div>
                                <div
                                    className="rounded-md border p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm"
                                    style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}
                                    data-testid="setup-mfa-recovery-codes"
                                >
                                    {recoveryCodes.map((c, i) => (
                                        <div key={i} className="tracking-wider" data-testid={`setup-mfa-recovery-code-${i}`}>
                                            {c}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button type="button" variant="outline"
                                        onClick={() => {
                                            const now = new Date().toISOString().slice(0, 10);
                                            const body = [
                                                "ASL HR Management Platform — Emergency Recovery Codes",
                                                `Account: ${user?.email || ""}`,
                                                `Generated: ${now}`,
                                                "",
                                                "Each code resets password + clears MFA + revokes sessions.",
                                                "Save OFFLINE. An alert is emailed to admins whenever one is used.",
                                                "",
                                                ...recoveryCodes,
                                                "",
                                            ].join("\n");
                                            const blob = new Blob([body], { type: "text/plain" });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement("a");
                                            a.href = url;
                                            a.download = `asl-recovery-codes-${now}.txt`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        }}
                                        data-testid="setup-mfa-download-recovery-codes"
                                    >
                                        <Download className="h-4 w-4 mr-1.5" /> Download recovery codes
                                    </Button>
                                    <Button type="button" variant="outline"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(recoveryCodes.join("\n"));
                                                toast.success("Recovery codes copied.");
                                            } catch { toast.error("Copy failed"); }
                                        }}
                                        data-testid="setup-mfa-copy-recovery-codes"
                                    >
                                        <Copy className="h-4 w-4 mr-1.5" /> Copy recovery codes
                                    </Button>
                                </div>
                            </div>
                        )}

                        <Button
                            type="button"
                            className="asl-navy-bg text-white w-full"
                            disabled={!acknowledged}
                            onClick={finish}
                            data-testid="setup-mfa-finish-button"
                        >
                            Done
                        </Button>
                    </div>
                )}
            </section>
        </AppShell>
    );
}
